import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { apiClient } from '../../config/api';

/**
 * Native Push Notifications Service (Firebase Cloud Messaging)
 */
export const NativePush = {
  /**
   * Request permission, register with FCM, and sync token with NEXDESK Backend
   * @param {Object} handlers Callbacks for registration, error, received, action
   */
  init: async ({ onRegistration, onRegistrationError, onNotificationReceived, onNotificationActionPerformed } = {}) => {
    if (!Capacitor.isNativePlatform()) {
      console.log('[Push] Skipped on web browser platform');
      return { status: 'unsupported' };
    }

    try {
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.warn('[Push] Notification permission was not granted by user');
        return { status: 'denied' };
      }

      // Add token registration listener
      await PushNotifications.addListener('registration', async (token) => {
        const tokenValue = token.value;
        console.log('[Push] Registration successful, FCM token:', tokenValue);
        
        // Save locally
        localStorage.setItem('fcm_device_token', tokenValue);

        // Sync with Backend if authenticated
        try {
          const authToken = localStorage.getItem('token');
          if (authToken) {
            await apiClient('/api/notifications/push-token', {
              method: 'POST',
              body: {
                token: tokenValue,
                platform: Capacitor.getPlatform() || 'android',
                device_info: {
                  userAgent: navigator.userAgent
                }
              }
            });
            console.log('[Push] Device token synced with NEXDESK backend API');
          }
        } catch (syncErr) {
          console.warn('[Push] Failed to sync push token with backend:', syncErr.message);
        }

        if (typeof onRegistration === 'function') {
          onRegistration(tokenValue);
        }
      });

      await PushNotifications.addListener('registrationError', (error) => {
        console.error('[Push] Registration error: ', JSON.stringify(error));
        if (typeof onRegistrationError === 'function') {
          onRegistrationError(error);
        }
      });

      // Foreground notification listener
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[Push] Received notification in foreground:', notification);
        if (typeof onNotificationReceived === 'function') {
          onNotificationReceived(notification);
        }
      });

      // Notification action/tap listener
      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('[Push] User tapped notification:', action);
        const data = action.notification?.data || {};

        // Custom action routing
        if (data.type === 'ticket_assigned' && data.ticket_id) {
          window.location.hash = `#/tickets?id=${data.ticket_id}`;
        } else if (data.type === 'webhook_ticket_assigned' && data.ticket_id) {
          window.location.hash = `#/tickets?id=${data.ticket_id}`;
        } else if (data.type === 'direct_chat' && data.sender_id) {
          window.location.hash = `#/chat?user=${data.sender_id}`;
        } else if (data.type === 'team_chat' && data.team_id) {
          window.location.hash = `#/teams?team=${data.team_id}`;
        }

        if (typeof onNotificationActionPerformed === 'function') {
          onNotificationActionPerformed(action);
        }
      });

      // Register with Firebase / APNs
      await PushNotifications.register();
      return { status: 'registered' };
    } catch (error) {
      console.error('[Push] Failed to initialize push notifications:', error);
      return { status: 'error', error };
    }
  },

  /**
   * Sync stored FCM token with backend when user logs in
   */
  syncTokenOnLogin: async () => {
    const token = localStorage.getItem('fcm_device_token');
    if (token && Capacitor.isNativePlatform()) {
      try {
        await apiClient('/api/notifications/push-token', {
          method: 'POST',
          body: {
            token,
            platform: Capacitor.getPlatform() || 'android'
          }
        });
        console.log('[Push] Existing token refreshed with backend on login');
      } catch (err) {
        console.warn('[Push] Error syncing token on login:', err.message);
      }
    }
  },

  /**
   * Unregister token on logout
   */
  unregisterOnLogout: async () => {
    const token = localStorage.getItem('fcm_device_token');
    if (token) {
      try {
        await apiClient('/api/notifications/push-token', {
          method: 'DELETE',
          body: { token }
        });
      } catch (err) {
        console.warn('[Push] Error unregistering token on logout:', err.message);
      }
    }
  },

  /**
   * Get list of delivered notifications
   */
  getDeliveredNotifications: async () => {
    if (Capacitor.isNativePlatform()) {
      return await PushNotifications.getDeliveredNotifications();
    }
    return { notifications: [] };
  },

  /**
   * Remove all delivered notifications from the notification center
   */
  removeAllDeliveredNotifications: async () => {
    if (Capacitor.isNativePlatform()) {
      await PushNotifications.removeAllDeliveredNotifications();
    }
  }
};

export default NativePush;
